import React, { useState } from 'react';
import { NetworkStatus } from '@apollo/client';
import { useRouteMatch } from 'react-router-dom';
import { Button, Grid, Typography, ListItemText, Card } from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { userLanguage } from '../../../../core/AuthContext';
import { ErrorComponent, LoadingSpinner } from '../../../../components';
import SymbolDetails from './SymbolDetails';
import EditIcon from '@material-ui/icons/Edit';

import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import UpdateSymbolCollectionDialog from '../SymbolCollectionUpdateDialog';
import SymbolCreateDialog from '../SymbolCreateDialog';
import { PlatformNavigation } from '../../navigation/PlatformNavigation';

const SymbolsCollectionDetails: React.FC<any> = ({ party }) => {
  const symbolCollectionId = PlatformNavigation.symbolCollectionDetails.paramsFromPath(useRouteMatch())
    .symbolCollectionId;

  const [errorComponentState, setErrorComponentState] = useState(false);
  const [showAddSymbolDialog, setShowAddSymbolDialog] = useState({ id: '', show: false });
  const [showUpdateSymbolCollectionDialog, setShowUpdateSymbolCollectionDialog] = useState({
    show: false
  });
  const { t: translate } = useTranslation();

  const {
    loading: symbolCollectionLoading,
    error: symbolCollectionError,
    data: symbolCollectionData,
    refetch: refetchSymbolCollection,
    networkStatus: symbolCollectionNetworkStatus
  } = GQLOperationBuilder.symbolCollection.getSymbolCollection({ symbolCollectionId: symbolCollectionId });

  const {
    loading: symbolListLoading,
    error: symbolListError,
    networkStatus: symbolListNetworkStatus,
    data: symbolListData,
    refetch: refetchSymbolsList
  } = GQLOperationBuilder.symbol.getSymbolsBySymbolCollection({ symbolCollectionId: symbolCollectionId });

  if (
    symbolListLoading ||
    symbolCollectionLoading ||
    symbolCollectionNetworkStatus === NetworkStatus.refetch ||
    symbolListNetworkStatus === NetworkStatus.refetch
  )
    return <LoadingSpinner />;

  if (symbolCollectionError && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={symbolCollectionError?.stack}
        message={symbolCollectionError.message}
        callback={() => refetchSymbolsList()}
      />
    );

  if (symbolListError && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={symbolCollectionError?.stack}
        message={symbolListError.message}
        callback={() => refetchSymbolCollection()}
      />
    );

  return (
    <Card style={{ margin: '10px 0', padding: '8px' }}>
      <div style={{ display: 'flex' }}>
        <Typography style={{ marginRight: '2px' }} variant="h4">
          {
            GQLOperationBuilder.symbolCollection.symbolCollectionFromResponse(symbolCollectionData).title[
              userLanguage
            ]
          }
        </Typography>
        <EditIcon
          fontSize="small"
          style={{ cursor: 'pointer' }}
          onClick={() => setShowUpdateSymbolCollectionDialog({ show: true })}
        />
      </div>

      <ListItemText
        primary="Collection Name"
        secondary={
          GQLOperationBuilder.symbolCollection.symbolCollectionFromResponse(symbolCollectionData).title[
            userLanguage
          ]
        }
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <ListItemText primary="Symbols" />

        <Button
          variant="contained"
          color="primary"
          onClick={() => setShowAddSymbolDialog({ id: symbolCollectionId, show: true })}
        >
          <AddIcon />
          {translate('sections.symbols.symbolCollectionDetails.addNewSymbol', 'Add Symbol')}
        </Button>
      </div>

      <Grid style={{ marginTop: '20px' }} container direction="row" spacing={2}>
        {GQLOperationBuilder.symbol
          .symbolAssetArrayFromResponse(symbolListData)
          .map(a => a)
          .sort((s1, s2) => s1.symbol.title.en!.localeCompare(s2.symbol.title.en!))
          .map((symbol, symbolIndex) => (
            <Grid key={symbolIndex} item xs={2} direction="column">
              <SymbolDetails key={symbolIndex} symbolAsset={symbol} party={party} reloadSymbols={() => {}} />
            </Grid>
          ))}
      </Grid>

      {showUpdateSymbolCollectionDialog.show && (
        <UpdateSymbolCollectionDialog
          dialogId="editSymbolCollectionDialog"
          party={party}
          symbolCollection={GQLOperationBuilder.symbolCollection.symbolCollectionFromResponse(
            symbolCollectionData
          )}
          onClose={val => {
            setShowUpdateSymbolCollectionDialog({ show: false });
            // reloadSymbols();
          }}
        ></UpdateSymbolCollectionDialog>
      )}

      {showAddSymbolDialog.show && (
        <SymbolCreateDialog
          party={party}
          dialogId="addSymbolDialog"
          symbolCollectionId={symbolCollectionId}
          onClose={val => {
            setShowAddSymbolDialog({ id: symbolCollectionId, show: false });
            // reloadSymbols();
          }}
        ></SymbolCreateDialog>
      )}
    </Card>
  );
};

export default SymbolsCollectionDetails;
